import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AIEngineBadge from "@/components/AIEngineBadge";
import Breadcrumb from "@/components/Breadcrumb";
import CreateAnalysisModal from "@/components/CreateAnalysisModal";
import CreateSampleModal from "@/components/CreateSampleModal";
import WorkflowProgress from "@/components/WorkflowProgress";
import { useAnalysesByCase, useCreateAnalysis } from "@/hooks/useAnalysis";
import { useAuth } from "@/hooks/useAuth";
import { useCaseDetail } from "@/hooks/useCases";
import { useCreateSample, useSampleList } from "@/hooks/useSamples";
import type { AnalysisType, Sample, SampleType } from "@/types";
import { apiErrorDetail } from "@/utils/apiError";
import {
  analysisStatusColor,
  analysisStatusLabels,
  analysisTypeLabels,
  caseStatusColor,
  caseStatusLabels,
  caseTypeColor,
  caseTypeLabels,
  sampleStatusColor,
  sampleStatusLabels,
  sampleTypeLabels,
} from "@/utils/labels";

type TabKey = "samples" | "analyses";

export default function CaseDetailPage() {
  const { id } = useParams();
  const caseId = Number(id);
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>("samples");
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [analysisTarget, setAnalysisTarget] = useState<Sample | null>(null);

  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
  } = useCaseDetail(caseId);

  const {
    data: samples,
    isLoading: samplesLoading,
    error: samplesError,
  } = useSampleList({ case_id: caseId });

  const { data: analyses, isLoading: analysesLoading } =
    useAnalysesByCase(caseId);

  const createSample = useCreateSample();
  const createAnalysis = useCreateAnalysis();
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const handleCreateSample = async (data: {
    sample_id: string;
    name: string;
    sample_type: SampleType;
    case_id: number;
  }) => {
    try {
      await createSample.mutateAsync(data);
      setShowSampleModal(false);
    } catch {
      alert("시료 등록에 실패했습니다.");
    }
  };

  const handleCreateAnalysis = (data: {
    sample_id: number;
    analysis_type: AnalysisType;
  }) => {
    setAnalysisError(null);
    createAnalysis.mutate(data, {
      onSuccess: (analysis) => {
        setAnalysisTarget(null);
        navigate(`/analyses/${analysis.id}`);
      },
      // onError 가 없으면 거절이 화면에 전혀 드러나지 않는다 (SVD-212-09).
      onError: (err) => {
        // 응답이 늦게 도착하는 사이 사용자가 다른 시료를 골랐다면 그 시료에
        // 남의 사유를 붙이지 않는다 — 경쟁 상황일수록 행 잠금 대기로 응답이
        // 늦어져 이 창이 넓어진다.
        setAnalysisTarget((current) => {
          if (current?.id === data.sample_id) {
            setAnalysisError(
              apiErrorDetail(
                err,
                "분석 생성에 실패했습니다. 잠시 후 다시 시도하세요.",
              ),
            );
          }
          return current;
        });
      },
    });
  };

  if (caseLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/cases"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            &larr; 뒤로
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse mb-8">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/4 mb-6" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (caseError) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/cases"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            &larr; 뒤로
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            사건 데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(caseError as Error).message}
          </p>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/cases"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            &larr; 뒤로
          </Link>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700 font-medium">
            사건을 찾을 수 없습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Breadcrumb
        items={[
          { label: "대시보드", to: "/" },
          { label: "사건관리", to: "/cases" },
          { label: caseData?.case_number ?? `사건 #${id}` },
        ]}
      />

      {/* 사건 정보 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {caseData.title}
            </h1>
            <p className="text-sm text-gray-500">
              사건번호: {caseData.case_number}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* T3: 화재잔해 클래스 모드 진입 (기획 v4 §4 두 모드 분리) */}
            <Link
              to={`/cases/${caseId}/fire-debris`}
              className="inline-flex px-3 py-1 text-xs font-semibold rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            >
              화재잔해 클래스 모드
            </Link>
            <span
              className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${caseTypeColor(caseData.case_type)}`}
            >
              {caseTypeLabels[caseData.case_type] ?? caseData.case_type}
            </span>
            <span
              className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${caseStatusColor(caseData.status)}`}
            >
              {caseStatusLabels[caseData.status] ?? caseData.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              발생 장소
            </h3>
            <p className="text-gray-900">{caseData.location || "-"}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              상세 위치
            </h3>
            <p className="text-gray-900">{caseData.location_detail || "-"}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              사건 발생일시
            </h3>
            <p className="text-gray-900">
              {caseData.incident_datetime
                ? new Date(caseData.incident_datetime).toLocaleString("ko-KR")
                : "-"}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">등록일</h3>
            <p className="text-gray-900">
              {new Date(caseData.created_at).toLocaleString("ko-KR")}
            </p>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-sm font-medium text-gray-500 mb-1">설명</h3>
            <p className="text-gray-900 whitespace-pre-wrap">
              {caseData.description || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* 워크플로우 진행률 (B4) */}
      <WorkflowProgress samples={samples ?? []} analyses={analyses ?? []} />

      {/* 탭 UI */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px px-6">
            <button
              type="button"
              onClick={() => setActiveTab("samples")}
              className={`py-4 px-4 border-b-2 font-medium text-sm cursor-pointer ${
                activeTab === "samples"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              시료 ({samples?.length ?? 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("analyses")}
              className={`py-4 px-4 border-b-2 font-medium text-sm cursor-pointer ${
                activeTab === "analyses"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              분석 ({analyses?.length ?? 0})
            </button>
          </nav>
        </div>

        {/* 시료 탭 */}
        {activeTab === "samples" && (
          <>
            {/* 시료 추가 버튼 (A1) */}
            {permissions?.canCreateSamples && (
              <div className="px-6 py-3 border-b border-gray-100 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowSampleModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  + 시료 추가
                </button>
              </div>
            )}

            {samplesLoading ? (
              <div className="p-6 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded mb-3" />
                ))}
              </div>
            ) : samplesError ? (
              <div className="p-6 text-center">
                <p className="text-red-600 font-medium">
                  시료 데이터를 불러오는 중 오류가 발생했습니다.
                </p>
                <p className="text-red-500 text-sm mt-1">
                  {(samplesError as Error).message}
                </p>
              </div>
            ) : samples && samples.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        시료 ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        이름
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        유형
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        상태
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        등록일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {samples.map((sample: Sample) => (
                      <tr key={sample.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/samples/${sample.id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          >
                            {sample.sample_id}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <Link
                            to={`/samples/${sample.id}`}
                            className="text-blue-600 hover:text-blue-800 cursor-pointer"
                          >
                            {sample.name}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {sampleTypeLabels[sample.sample_type] ??
                            sample.sample_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${sampleStatusColor(sample.status)}`}
                          >
                            {sampleStatusLabels[sample.status] ?? sample.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(sample.created_at).toLocaleDateString(
                            "ko-KR",
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {/* 분석 시작 버튼 (B1) */}
                          {permissions?.canRunAnalysis && (
                            <button
                              type="button"
                              onClick={() => {
                                // 대상이 바뀌면 이전 시료의 실패 사유를 지운다 —
                                // 남겨 두면 진행 중 분석이 없는 시료에 대고
                                // "이미 진행 중"이라고 말한다 (SVD-212-09 리뷰).
                                setAnalysisError(null);
                                setAnalysisTarget(sample);
                              }}
                              className="text-green-600 hover:text-green-800 font-medium cursor-pointer"
                            >
                              분석 시작
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <p className="text-lg font-medium text-gray-900 mb-2">
                  등록된 시료가 없습니다
                </p>
                <p className="text-gray-500 mb-4">
                  이 사건에 등록된 시료가 아직 없습니다.
                </p>
                {permissions?.canCreateSamples && (
                  <button
                    type="button"
                    onClick={() => setShowSampleModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    + 시료 추가
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* 분석 탭 */}
        {activeTab === "analyses" &&
          (analysesLoading ? (
            <div className="p-6 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 bg-gray-200 rounded mb-3" />
              ))}
            </div>
          ) : analyses && analyses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      분석 유형
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      시료 ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AI 엔진
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      주요 화합물
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyses.map((analysis) => (
                    <tr key={analysis.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/analyses/${analysis.id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          #{analysis.id}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {analysisTypeLabels[analysis.analysis_type] ??
                          analysis.analysis_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${analysisStatusColor(analysis.status)}`}
                        >
                          {analysisStatusLabels[analysis.status] ??
                            analysis.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <Link
                          to={`/samples/${analysis.sample_id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {analysis.sample_id}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <AIEngineBadge
                          engine={
                            analysis.model_version?.includes("inha")
                              ? "inha_jsd"
                              : "mock"
                          }
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {analysis.results[0]?.compound_name ?? "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link
                          to={`/analyses/${analysis.id}/dashboard`}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          대시보드
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-lg font-medium text-gray-900 mb-2">
                분석 결과가 없습니다
              </p>
              <p className="text-gray-500">
                이 사건에 대한 분석이 아직 실행되지 않았습니다.
              </p>
            </div>
          ))}
      </div>

      {/* 시료 등록 모달 (A1) */}
      <CreateSampleModal
        caseId={caseId}
        caseName={caseData.title}
        isOpen={showSampleModal}
        onClose={() => setShowSampleModal(false)}
        onSubmit={handleCreateSample}
        isLoading={createSample.isPending}
      />

      {/* 분석 생성 모달 (B1) */}
      {analysisTarget && (
        <CreateAnalysisModal
          sampleId={analysisTarget.id}
          sampleName={analysisTarget.name}
          isOpen={!!analysisTarget}
          onClose={() => {
            setAnalysisTarget(null);
            setAnalysisError(null);
          }}
          onSubmit={handleCreateAnalysis}
          isLoading={createAnalysis.isPending}
          error={analysisError}
        />
      )}
    </div>
  );
}
